import { Pause, Play, Trash2, X } from 'lucide-react'
import { useId, useRef, useState, type FormEvent, type RefObject } from 'react'
import { Icon } from '../../platform/icons/Icon'
import { useToast } from '../../platform/feedback/ToastProvider'
import { Overlay } from '../../platform/overlay/Overlay'
import { ConfirmDialog } from '../../platform/primitives/Dialog'
import { Button } from '../../platform/primitives/Button'
import { Field } from '../../platform/primitives/Field'
import { AlertPanel } from '../../platform/primitives/Panel'
import { SegmentedControl } from '../../platform/primitives/SegmentedControl'
import { Select } from '../../platform/primitives/Select'
import { TagEditor } from '../../platform/primitives/TagEditor'
import { useBreakpoint } from '../../platform/responsive/useBreakpoint'
import { DeleteConfirm } from './BillLifecycle'
import {
  SETTINGS_COPY,
  dedupeTags,
  draftOfBill,
  reminderNote,
  reminderOptions,
  sameDraft,
  settingsBody,
  validateSettings,
  type SettingsDraft,
  type SettingsErrors,
} from './billSettingsDraft'
import type { Bill } from './recurringWire'
import { CONFLICT_COPY, LifecycleError, PAUSE_FAILED, RESUME_FAILED, useLifecycle } from './useLifecycle'
import { SettingsSaveError, useSaveBillSettings } from './useSaveBillSettings'
import { SAVE_COPY } from './useSaveRecurring'
import './billSettings.css'

const DISCARD = {
  title: 'Discard changes?',
  body: "Your changes to this bill won't be saved.",
  confirm: 'Discard changes',
  keep: 'Keep editing',
} as const

const AMOUNT_TYPES = [
  { value: 'variable', label: 'Variable' },
  { value: 'fixed', label: 'Fixed' },
] as const

/** What the panel was last seeded from: the fields, and the `version` they were read at. */
type Seed = { version: number; fields: SettingsDraft }

/**
 * Bill settings (M7 Task 12, D5): a right panel on a tablet and up, a sheet on a
 * phone. Reminder, Detection and Lifecycle.
 *
 * **One Save, one PATCH, with the `version` the user last saw.** Only the keys
 * that differ from the seed are sent, so an untouched field is never rewritten.
 * Fixed with no expected amount is refused here and nothing is sent.
 *
 * **Pause, Resume and Delete act at once** (S10) and do not wait for Save.
 *
 * **Re-seeding (S13, Review Focus 4).** The panel holds the version it seeded
 * from. When the bill it is given moves to a new version:
 * - and none of the fields it edits changed (a pause bumps the version, or
 *   another device edited something else), the version is simply taken and the
 *   user's unsaved edits stay — otherwise their next Save would 409 on their
 *   own pause;
 * - and one of the fields it edits changed, someone else wrote them: every
 *   field is re-seeded from the latest and the S13 sentence says so. That is
 *   what a 409 produces too, once the refetch lands.
 *
 * Derived during render rather than in an effect, so a re-seed never paints one
 * frame of the old values.
 */
export function BillSettings({
  bill,
  onClose,
  restoreFocusTo,
}: {
  bill: Bill
  onClose: () => void
  /** The detail's Bill settings button, which focus returns to on close. */
  restoreFocusTo: RefObject<HTMLElement | null> | undefined
}) {
  const bp = useBreakpoint()
  const toast = useToast()
  const [seed, setSeed] = useState<Seed>(() => ({ version: bill.version, fields: draftOfBill(bill) }))
  const [draft, setDraft] = useState<SettingsDraft>(seed.fields)
  const [errors, setErrors] = useState<SettingsErrors>({})
  const [notice, setNotice] = useState<string | null>(null)
  const [lifecycleError, setLifecycleError] = useState<string | null>(null)
  const [discarding, setDiscarding] = useState(false)
  const [deleting, setDeleting] = useState(false)
  const expectedRef = useRef<HTMLInputElement>(null)
  const submitting = useRef(false)

  if (bill.version !== seed.version) {
    const latest = draftOfBill(bill)
    if (sameDraft(latest, seed.fields)) {
      setSeed({ version: bill.version, fields: seed.fields })
    } else {
      setSeed({ version: bill.version, fields: latest })
      setDraft(latest)
      setErrors({})
      setNotice(CONFLICT_COPY)
    }
  }

  const { save, saving } = useSaveBillSettings(bill)
  const { setPaused, busy: lifecycleBusy } = useLifecycle(bill)
  const dirty = !sameDraft(draft, seed.fields)
  const locked = saving || lifecycleBusy

  const ids = { reminder: useId(), detection: useId(), lifecycle: useId(), tags: useId() }
  const noteIds = { reminder: useId(), amount: useId(), pause: useId(), del: useId() }

  function change(patch: Partial<SettingsDraft>, clears?: keyof SettingsErrors) {
    setDraft((d) => ({ ...d, ...patch }))
    if (clears) setErrors((e) => ({ ...e, [clears]: undefined }))
  }

  function requestClose() {
    if (dirty && !submitting.current) setDiscarding(true)
    else onClose()
  }

  async function submit(e: FormEvent) {
    e.preventDefault()
    if (submitting.current || locked) return
    setNotice(null)
    const found = validateSettings(draft)
    setErrors(found)
    if (found.expected !== undefined) {
      expectedRef.current?.focus()
      return
    }
    if (found.reminder !== undefined) return
    const body = settingsBody(seed.fields, draft, seed.version)
    if (body === null) {
      onClose()
      return
    }
    submitting.current = true
    try {
      await save(body)
    } catch (err) {
      if (err instanceof SettingsSaveError && err.kind === 'removed') {
        // The bill is gone; the detail shows its removed state behind us.
        onClose()
        return
      }
      setNotice(err instanceof SettingsSaveError ? err.message : SAVE_COPY.failed)
      return
    } finally {
      submitting.current = false
    }
    toast.show({ kind: 'success', message: `${bill.name} saved` })
    onClose()
  }

  async function togglePause() {
    if (locked) return
    setLifecycleError(null)
    try {
      await setPaused(!bill.isPaused, { quiet: true })
    } catch (err) {
      setLifecycleError(err instanceof LifecycleError ? err.message : bill.isPaused ? RESUME_FAILED : PAUSE_FAILED)
    }
  }

  const paused = bill.isPaused
  const fixed = draft.amountType === 'fixed'
  const pausing = lifecycleBusy
  const pauseLabel = pausing ? (paused ? 'Pausing…' : 'Resuming…') : paused ? 'Resume' : 'Pause'

  return (
    <Overlay
      open
      label="Bill settings"
      placement={bp === 'phone' ? 'sheet' : 'right'}
      onClose={requestClose}
      restoreFocusTo={restoreFocusTo}
    >
      <form className="bs" onSubmit={(e) => void submit(e)} noValidate>
        <header className="bs__head">
          <div className="bs__titles">
            <h2 className="bs__title">Bill settings</h2>
            <p className="bs__bill">{bill.name}</p>
          </div>
          <Button type="button" variant="chip" size="panel" aria-label="Close" onClick={requestClose}>
            <Icon glyph={X} size={16} />
          </Button>
        </header>

        <div className="bs__body">
          <section className="bs__section" aria-labelledby={ids.reminder}>
            <h3 id={ids.reminder} className="bs__heading">
              Reminder
            </h3>
            <Select
              label="Remind me"
              size="form"
              value={String(draft.reminderDays)}
              aria-describedby={noteIds.reminder}
              onChange={(e) => change({ reminderDays: Number(e.target.value) }, 'reminder')}
            >
              {reminderOptions(draft.reminderDays).map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </Select>
            <p id={noteIds.reminder} className="note">
              {reminderNote(draft.reminderDays)}
            </p>
            {errors.reminder && <p className="bs__error">{errors.reminder}</p>}
          </section>

          <section className="bs__section" aria-labelledby={ids.detection}>
            <h3 id={ids.detection} className="bs__heading">
              Detection
            </h3>
            <dl className="bs__merchant">
              <dt className="bs__label">Merchant</dt>
              <dd className="bs__value">{bill.merchant || 'Not set'}</dd>
            </dl>
            <p className="note">
              {SETTINGS_COPY.merchantNote}
            </p>

            <div className="bs__group">
              <span className="bs__label" aria-hidden="true">
                Amount
              </span>
              <SegmentedControl
                label="Amount"
                name="amount_type"
                size="form"
                options={AMOUNT_TYPES}
                value={draft.amountType}
                describedBy={noteIds.amount}
                onChange={(amountType) => change({ amountType }, 'expected')}
              />
              <p id={noteIds.amount} className="note">
                {fixed ? SETTINGS_COPY.fixedNote : SETTINGS_COPY.variableNote}
              </p>
            </div>

            {fixed && (
              <Field
                ref={expectedRef}
                label={`Expected amount (${bill.currency})`}
                size="form"
                placeholder="0.00"
                inputMode="decimal"
                autoComplete="off"
                value={draft.expectedText}
                error={errors.expected}
                onChange={(e) => change({ expectedText: e.target.value }, 'expected')}
              />
            )}

            <div className="bs__group">
              <span className="bs__label" id={ids.tags}>
                Tags
              </span>
              <TagEditor
                tags={draft.tags}
                suggestions={[]}
                disabled={false}
                reasonId={undefined}
                labelledBy={ids.tags}
                onChange={(next) => change({ tags: dedupeTags(next) })}
              />
              {draft.tags.length === 0 && <p className="note">No tags yet.</p>}
            </div>
          </section>

          <section className="bs__section" aria-labelledby={ids.lifecycle}>
            <h3 id={ids.lifecycle} className="bs__heading">
              Lifecycle
            </h3>
            <div className="bs__group">
              <Button
                type="button"
                variant="outline"
                size="panel"
                className="bs__action"
                aria-disabled={locked || undefined}
                aria-describedby={noteIds.pause}
                onClick={() => void togglePause()}
              >
                <Icon glyph={paused ? Play : Pause} size={14} />
                {pauseLabel}
              </Button>
              <p id={noteIds.pause} className="note">
                {paused ? SETTINGS_COPY.pausedNote : SETTINGS_COPY.pauseNote}
              </p>
              {lifecycleError && (
                <p role="alert" className="bs__error">
                  {lifecycleError}
                </p>
              )}
            </div>
            <div className="bs__group">
              <Button
                type="button"
                variant="destructive"
                size="panel"
                className="bs__action"
                aria-disabled={locked || undefined}
                aria-describedby={noteIds.del}
                onClick={() => {
                  if (!locked) setDeleting(true)
                }}
              >
                <Icon glyph={Trash2} size={14} />
                Delete
              </Button>
              <p id={noteIds.del} className="note">
                {SETTINGS_COPY.deleteNote}
              </p>
            </div>
          </section>
        </div>

        {notice !== null && (
          <AlertPanel className="bs__alert">
            <span role="alert">{notice}</span>
          </AlertPanel>
        )}

        <footer className="bs__foot">
          <Button type="button" variant="chip" size="panel" onClick={requestClose}>
            Cancel
          </Button>
          <Button type="submit" variant="primary" size="panel" aria-disabled={saving || undefined}>
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </footer>
      </form>

      <ConfirmDialog
        open={discarding}
        title={DISCARD.title}
        body={DISCARD.body}
        confirmLabel={DISCARD.confirm}
        cancelLabel={DISCARD.keep}
        destructive
        mode="discard_bill_settings"
        onCancel={() => setDiscarding(false)}
        onConfirm={async () => {
          setDiscarding(false)
          onClose()
        }}
      />
      {deleting && <DeleteConfirm bill={bill} onClose={() => setDeleting(false)} />}
    </Overlay>
  )
}
